import React from 'react'
import { transparentize } from 'polished'
import styled, { css } from 'styled-components'
import theme from 'styled-theming'

import ImageWithBlurryPlaceholder from '../ImageWithBlurryPlaceholder'
import NavLink from '../NavLink'
import {
  COLORS,
  CSS_ELEMENTS_RESETS,
  focusWithIn,
  hover,
  focus,
  media,
  typeScale,
  THEME,
  dropShadow,
  onlyForSafari,
} from '../../styles'
import RecipeAuthorBadge from '../RecipeAuthorBadge'
import PricePerPortionBadge from '../PricePerPortionBadge'

const DIRECTION = {
  top: theme('direction', {
    vertical: '6rem',
    horizontal: '0',
  }),
  topMedium: theme('direction', {
    vertical: '8rem',
    horizontal: '0',
  }),
  imageBorderRadius: theme('direction', {
    vertical: '1.6rem 1.6rem 0 0',
    horizontal: '1.6rem 0 0 1.6rem',
  }),
  imageWidth: theme('direction', {
    vertical: '100%',
    horizontal: '12.8rem',
  }),
  imageContainerPaddingTop: theme('direction', {
    vertical: '100%',
    horizontal: 'unset',
  }),
  imagePaddingTop: theme('direction', {
    vertical: '75%',
    horizontal: 'unset',
  }),
  imageHeight: theme('direction', {
    vertical: 'auto',
    horizontal: '100%',
  }),
  content: theme('direction', {
    vertical:
      'display: flex; flex-flow: column nowrap; align-items: center; justify-content: center; height: 100%;',
    horizontal: 'display: flex; flex-flow: row nowrap; align-items: center;',
  }),
  thumbnailPreserveSpace: theme('direction', {
    vertical:
      'position: absolute; bottom: 2.4rem; left: 0; right: 0; width: 100%; height: auto;',
    horizontal: 'position: relative;',
  }),
  text: theme('direction', {
    vertical:
      'display: flex; flex-flow: column nowrap; align-items: center; justify-content: center; flex: 1;',
    horizontal: '',
  }),
  textAlign: theme('direction', {
    vertical: 'center',
    horizontal: 'left',
  }),
  cardPadding: theme('direction', {
    vertical: '0',
    horizontal: '0 1.2rem 0 0',
  }),
  cardPaddingMedium: theme('direction', {
    vertical: '0',
    horizontal: '0 2.4rem 0 0',
  }),
  textMargin: theme('direction', {
    vertical: '-1.6rem 0 0 0',
    horizontal: '0',
  }),
  textMarginMedium: theme('direction', {
    vertical: '-1.6rem 0 0 0',
    horizontal: '0',
  }),
  textPadding: theme('direction', {
    vertical: '0.8rem 1.2rem 1.2rem',
    horizontal: '1.2rem 1.2rem 0 1.2rem',
  }),
  textPaddingMedium: theme('direction', {
    vertical: '0.8rem 0.8rem 1.2rem',
    horizontal: '2.4rem 0 2.4rem 2.4rem',
  }),
  authorMargin: theme('direction', {
    vertical: '0 auto 0.8rem',
    horizontal: '0 auto 0.8rem 0',
  }),
}

const VARIANTS = {
  border: theme('variant', {
    main: '0',
    profile: `0.1rem solid ${THEME.COLORS.foreground.tertiary}`,
    drawer: `0.1rem solid ${THEME.COLORS.foreground.tertiary}`,
    embed: `0.1rem solid ${THEME.COLORS.foreground.tertiary}`,
  }),
  thumbnailPreserveSpace: theme('variant', {
    main: '0',
    profile: 'left: -0.1rem; right: -0.1rem; margin: 0; width: auto;',
    drawer: 'left: -0.1rem; right: -0.1rem; margin: 0; width: auto;',
    embed: 'left: -0.1rem; right: -0.1rem; margin: 0; width: auto;',
  }),
  thumbnailPreserveSpaceSafari: theme('variant', {
    main: '0',
    profile: 'left: -1.6px; right: -1.6px;',
    drawer: 'left: -1.6px; right: -1.6px;',
    embed: 'left: -1.6px; right: -1.6px;',
  }),
}

const Styled = styled.article`
  min-height: 100%;
  width: 100%;
  position: relative;
  padding-top: ${DIRECTION.top};
  ${media.medium`
    padding-top: ${DIRECTION.topMedium};
  `}
`
Styled.thumbnailPreserveSpace = styled.div`
  padding-top: ${DIRECTION.imageContainerPaddingTop};
  margin-top: -${DIRECTION.top};
  position: relative;
  width: ${DIRECTION.imageWidth};
  height: ${DIRECTION.imageHeight};
  ${media.medium`
    margin-top: -${DIRECTION.topMedium};
  `}
`
Styled.thumbnailPreserveSpaceContainer = styled.div`
  height: ${DIRECTION.imageHeight};

  ${DIRECTION.thumbnailPreserveSpace};
  ${VARIANTS.thumbnailPreserveSpace};
  /* TODO: remove me when it will be fixed
      (see https://nextjs.org/docs/api-reference/next/future/image#known-browser-bugs) */
  ${onlyForSafari`
    ${VARIANTS.thumbnailPreserveSpaceSafari}
  `}
`
Styled.thumbnailContainer = styled.div`
  padding-top: ${DIRECTION.imagePaddingTop};
  position: relative;
  transition: transform 0.2s ease-in-out;
  width: ${DIRECTION.imageWidth};
  height: ${DIRECTION.imageHeight};
  background-color: unset;
  border-radius: ${DIRECTION.imageBorderRadius};
  ${({ backgroundPatternUrl, imageLoaded }) =>
    backgroundPatternUrl
      ? `
    background-image: url('${backgroundPatternUrl}');
    background-size: cover;
  `
      : `
    background-color: ${
      imageLoaded ? 'none' : THEME.COLORS.background.tertiary
    };
  `};
`
Styled.thumbnail = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  display: block;
  transition-property: opacity;
  transition-duration: 0.2s;
  overflow: hidden;
  border-radius: ${DIRECTION.imageBorderRadius};
`
Styled.thumbnailWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  opacity: ${({ isCooked }) => (isCooked ? '0.5' : '1')};
  transition: opacity 0.2s ease-in-out;
  background-color: ${({ isPlaceholder }) =>
    isPlaceholder ? 'unset' : COLORS.white};

  img {
    ${({ isPlaceholder, imageLoaded }) =>
      dropShadow(
        {
          offsetX: '0.4rem',
          offsetY: '0.8rem',
          color: transparentize(0.4, COLORS.black),
        },
        imageLoaded && isPlaceholder,
      )}
  }
`
Styled.author = styled(RecipeAuthorBadge)`
  margin: ${DIRECTION.authorMargin};
  display: inline-block;
`
Styled.title = styled.p`
  ${typeScale('body')};
  color: ${({ isCooked }) =>
    isCooked
      ? THEME.COLORS.foreground.secondary
      : THEME.COLORS.foreground.primary};
  margin: 0;
  transition:
    color 0.2s ease-in-out,
    text-decoration 0.2s ease-in-out;
  width: 100%;
  text-decoration: ${({ isCooked }) => (isCooked ? 'line-through' : 'none')};
`
Styled.description = styled.p`
  display: none;
  ${media.medium`
    ${typeScale('caption')};
    color: ${THEME.COLORS.foreground.secondary};
    margin: 0;
    padding-top: 0.4rem;
    width: 100%;
    display: block;
  `}
`
Styled.labels = styled.p`
  padding-top: 0;
`
Styled.badgeEdito = styled.span`
  ${typeScale('caption')};
  padding: 0.3rem 0.6rem;
  border: 0;
  background-color: ${COLORS.radish};
  color: ${COLORS.beet};
  border-radius: 9999px;
  display: inline-block;
  white-space: nowrap;
  margin: 0.4rem 0.2rem 0;
`
Styled.tag = styled.span`
  ${typeScale('caption')};
  padding: 0.3rem 0.6rem;
  border: 0;
  background-color: ${COLORS.radish};
  color: ${COLORS.beet};
  border-radius: 9999px;
  display: inline-block;
  white-space: nowrap;
  margin: 0.8rem 0.4rem 0;
`
Styled.text = styled.div`
  ${DIRECTION.text};
  color: ${THEME.COLORS.foreground.primary};
  margin: ${DIRECTION.textMargin};
  padding: ${DIRECTION.textPadding};
  text-align: ${DIRECTION.textAlign};
  width: 100%;
  ${media.medium`
    padding: ${DIRECTION.textPaddingMedium};
    margin: ${DIRECTION.textMarginMedium};
  `}
  ${({ isLoading }) =>
    isLoading
      ? ''
      : css`
          ${Styled.title} {
            display: -webkit-box;
            overflow: hidden;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 4;
            ${media.medium`
              -webkit-line-clamp: 3;
            `}
          }
          ${Styled.description} {
            display: none;
            ${media.medium`
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              overflow: hidden;
            `}
          }
        `};
`
Styled.content = styled.div`
  margin: 0;
  width: 100%;
  position: relative;
  ${DIRECTION.content}
`
Styled.wrapper = styled(({ to, href, children, isCooked, ...rest }) =>
  href ? (
    <a href={href} {...rest}>
      {children}
    </a>
  ) : to ? (
    <NavLink to={to} {...rest}>
      {children}
    </NavLink>
  ) : (
    <div {...rest}>{children}</div>
  ),
)`
  display: block;
  width: 100%;
  text-decoration: none;
  flex: 1;
  ${({ to, isCooked }) =>
    to && !isCooked
      ? hover`
        ${Styled.title} {
          color: ${COLORS.primary};
        }
      `
      : ''};
  ${focus`
    outline: 0;
  `};
`
Styled.titlePlaceholder = styled.span`
  background-color: ${THEME.COLORS.background.tertiary};
  width: 70%;
  display: inline-block;
  height: 1.6rem;

  &:last-of-type {
    width: 40%;
  }
`
Styled.container = styled.div`
  background-color: ${THEME.COLORS.background.secondary};
  border-radius: 1.6rem;
  border: ${VARIANTS.border};
  padding: ${DIRECTION.cardPadding};
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
  width: 100%;
  ${media.medium`
    padding: ${DIRECTION.cardPaddingMedium};
  `}
  ${({ isCooked }) =>
    isCooked
      ? ''
      : focusWithIn`
    ${Styled.title} {
      color: ${COLORS.primary};
    }
  `}
`
Styled.descriptionPlaceholder = styled(Styled.titlePlaceholder)`
  width: 90%;
  height: 1rem;

  &:last-of-type {
    width: 60%;
  }
`
Styled.missingIngredients = styled.button`
  ${CSS_ELEMENTS_RESETS.button};
  cursor: help;
  margin: 0.8rem auto 0.4rem;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${COLORS.primary};
  transition: color 0.2s ease-in-out;
`
Styled.thumbnailLayer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  inset: 0;
`
Styled.thumbnailLayerContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`
Styled.imageWithBlurryPlaceholder = styled(ImageWithBlurryPlaceholder)`
  object-fit: ${({ objectFit }) => objectFit || 'contain'};
`
Styled.pricePerPortionBadge = styled(PricePerPortionBadge)`
  position: absolute;
  right: 0.4rem;
  top: -1.6rem;
`

export default Styled
